
import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { ConnectionStatus } from '../types';
import { getStudioApiKey, base64ToBytes, decodeAudioData, bytesToBase64 } from '../utils/audio';
import Visualizer from './Visualizer';

const MODEL_NAME = 'gemini-2.5-flash-native-audio-preview-12-2025';

interface LiveSessionProps {
  ui: any;
  onStatusChange?: (status: ConnectionStatus) => void;
}

const LiveSession: React.FC<LiveSessionProps> = ({ ui, onStatusChange }) => {
  const t = ui?.live || { title: "Livestream Studio", connected: "Live Uplink", offline: "Offline", logs_title: "System Logs" };
  const [status, setStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  const [logs, setLogs] = useState<string[]>([]);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sessionRef = useRef<any>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  const apiKey = getStudioApiKey();

  const addLog = (msg: string) => {
    setLogs(prev => [...prev.slice(-15), `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  useEffect(() => { onStatusChange?.(status); }, [status, onStatusChange]);

  const encodePCM = (data: Float32Array): string => {
    const l = data.length;
    const int16 = new Int16Array(l);
    for (let i = 0; i < l; i++) {
      int16[i] = data[i] * 32768;
    }
    return bytesToBase64(new Uint8Array(int16.buffer));
  };

  const cleanupAudio = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch(e) {}
        audioContextRef.current = null;
    }
    if (inputAudioContextRef.current) {
        try { inputAudioContextRef.current.close(); } catch(e) {}
        inputAudioContextRef.current = null;
    }
    sourcesRef.current.forEach(source => { try { source.stop(); } catch(e) {} });
    sourcesRef.current.clear();
    nextStartTimeRef.current = 0;
  };

  const startSession = async () => {
    if (!apiKey) { addLog("Uplink Aborted: Vault Key Missing."); return; }
    try {
      setStatus(ConnectionStatus.CONNECTING);
      addLog("Initializing Gemini Live Pipeline...");
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const inputCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      inputAudioContextRef.current = inputCtx;
      
      const outputCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      audioContextRef.current = outputCtx;
      
      analyserRef.current = outputCtx.createAnalyser();
      const outputNode = outputCtx.createGain();
      outputNode.connect(analyserRef.current);
      analyserRef.current.connect(outputCtx.destination);

      const ai = new GoogleGenAI({ apiKey });
      const sessionPromise = ai.live.connect({
        model: MODEL_NAME,
        callbacks: {
          onopen: () => {
            const source = inputCtx.createMediaStreamSource(stream);
            const scriptProcessor = inputCtx.createScriptProcessor(4096, 1, 1);
            scriptProcessor.onaudioprocess = (audioProcessingEvent) => {
              const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
              const pcmData = encodePCM(inputData);
              sessionPromise.then((session) => {
                session.sendRealtimeInput({ 
                  media: { data: pcmData, mimeType: 'audio/pcm;rate=16000' } 
                });
              });
            };
            source.connect(scriptProcessor);
            scriptProcessor.connect(inputCtx.destination);
            
            setStatus(ConnectionStatus.CONNECTED);
            addLog("Zero-Latency Handshake Established.");
          },
          onmessage: async (message: LiveServerMessage) => {
            // Audit Insight: Robust optional chaining for modelTurn parts to resolve TS error
            const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
            if (base64Audio && audioContextRef.current) {
              const ctx = audioContextRef.current;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              
              const audioBuffer = await decodeAudioData(
                base64ToBytes(base64Audio),
                ctx,
                24000,
                1
              );
              
              const source = ctx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(outputNode);
              source.addEventListener('ended', () => {
                sourcesRef.current.delete(source);
              });

              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              sourcesRef.current.add(source);
            }

            if (message.serverContent?.interrupted) {
              addLog("Model Interrupted.");
              for (const source of sourcesRef.current.values()) {
                source.stop();
                sourcesRef.current.delete(source);
              }
              nextStartTimeRef.current = 0;
            }
          },
          onerror: (e) => { 
            console.error("LiveStream Fault:", e);
            addLog("Signal Interrupted. Pipeline error."); 
            setStatus(ConnectionStatus.ERROR); 
          },
          onclose: () => { 
            setStatus(ConnectionStatus.DISCONNECTED); 
            addLog("Signal Terminated."); 
            cleanupAudio();
          }
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Zephyr' } },
          },
          systemInstruction: 'You are Eve. A Sydney-based creative lead at GrooveFX Studio. Professional, visionary, smart. Your lilt is Sydney Australian. Focus on real-time creative direction. You are using the low-latency Gemini Live API.',
        },
      });
      sessionRef.current = await sessionPromise;
    } catch (e) { 
      setStatus(ConnectionStatus.ERROR); 
      addLog("Critical Link Failure.");
      cleanupAudio();
    }
  };

  const stopSession = () => {
    if (sessionRef.current) {
        try { sessionRef.current.close(); } catch(e) {}
        sessionRef.current = null;
    }
    cleanupAudio();
    setStatus(ConnectionStatus.DISCONNECTED);
  };

  const isConnected = status === ConnectionStatus.CONNECTED;

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-140px)] space-y-4 md:space-y-6 max-w-6xl mx-auto p-4 select-none relative overflow-hidden">
      {/* STATUS BAR */}
      <div className="flex items-center justify-between bg-black/50 p-6 md:p-8 rounded-[2.5rem] md:rounded-[3rem] border border-white/10 backdrop-blur-3xl shrink-0 z-10 shadow-2xl text-left">
        <div className="flex flex-col">
          <h2 className="text-xl md:text-4xl font-black text-white uppercase tracking-tighter leading-none">
             <i className="fa-solid fa-satellite-dish text-groove-accent mr-3 md:mr-5"></i>
             {t.title}
          </h2>
          <p className="text-[8px] md:text-[9px] text-slate-500 font-black uppercase tracking-[0.4em] mt-1 ml-1 leading-none">Native Audio Pipeline // Sydney Uplink</p>
        </div>
        <div className="flex items-center gap-3 md:gap-4 px-4 py-2 rounded-full bg-white/5 border border-white/10">
            <span className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-600 shadow-[0_0_10px_#ef4444]'}`}></span>
            <span className="text-[9px] md:text-xs font-black uppercase text-slate-300 tracking-widest">{status}</span>
        </div>
      </div>

      {/* CORE HUB */}
      <div className="flex-1 min-h-[300px] bg-black/40 rounded-[3rem] md:rounded-[4rem] border border-white/10 relative overflow-hidden flex flex-col items-center justify-center shadow-3xl z-20 mx-2">
        <div className="absolute inset-0 z-0 opacity-40">
           <Visualizer analyser={analyserRef.current} isActive={isConnected} />
        </div>
        
        <div className="relative z-50 flex flex-col items-center justify-center space-y-6">
            <button onClick={isConnected ? stopSession : startSession}
              className={`w-32 h-32 md:w-44 md:h-44 rounded-full flex flex-col items-center justify-center border-[6px] md:border-[10px] transition-all active:scale-95 shadow-2xl group
              ${isConnected ? 'bg-green-500 border-white text-white shadow-green-500/30' : 'bg-slate-900 border-white/10 text-white hover:border-groove-pink hover:bg-groove-pink/10'}`}
            >
              <i className={`fa-solid ${isConnected ? 'fa-phone-slash' : 'fa-power-off'} text-3xl md:text-5xl mb-2 group-hover:scale-110 transition-transform`}></i>
              <span className="text-[9px] md:text-10px font-black uppercase tracking-[0.4em]">{isConnected ? 'END' : 'LINK'}</span>
            </button>
            {!isConnected && (
              <p className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.6em] text-slate-700 animate-pulse">Awaiting Signal</p>
            )}
        </div>
      </div>

      {/* SYSTEM LOGS */}
      <div className="h-32 md:h-40 bg-black/60 rounded-[2.5rem] md:rounded-[3rem] border border-white/10 p-6 md:p-8 overflow-y-auto custom-scrollbar font-mono text-[9px] md:text-[10px] text-slate-500 shrink-0 shadow-2xl z-10 mx-2 mb-2 text-left">
         <p className="text-[8px] font-black uppercase tracking-widest text-slate-700 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-terminal text-groove-accent"></i> {t.logs_title}
         </p>
         <div className="space-y-1 md:space-y-2 opacity-70">
           {logs.map((l, i) => <div key={i} className="mb-1 border-l border-white/10 pl-4 leading-relaxed">{l}</div>)}
         </div>
      </div>
    </div>
  );
};

export default LiveSession;
